import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Seo from "../components/Seo";
import JsonLd from "../components/JsonLd";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ArrowRight,
  Search,
  Wind,
  Droplet,
  Zap,
  Sparkles,
  Hammer,
  Bug,
  Scissors,
  MapPin,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const iconMap = {
  wind: Wind,
  droplet: Droplet,
  zap: Zap,
  sparkles: Sparkles,
  "paint-bucket": Sparkles,
  hammer: Hammer,
  bug: Bug,
  scissors: Scissors,
};

const LOCATION_KEY = "tut_saved_location";

export default function Services() {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [selectedService, setSelectedService] = useState(null);
  const [savedLocation, setSavedLocation] = useState(null);

  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  const getLocationName = async (lat, lng) => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`);
      if (!res.ok) throw new Error("Reverse geocoding failed");
      const data = await res.json();
      return data.display_name || "Saved current location";
    } catch {
      return "Saved current location";
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/services").then((r) => {
      setServices(r.data);
      setLoading(false);
    }).catch(() => { setLoading(false); toast.error(t("common.failed")); });
    const lat = params.get("lat");
    const lng = params.get("lng");
    if (lat && lng) {
      setSavedLocation({
        lat,
        lng,
        label: "Saved current location",
      });
      getLocationName(lat, lng).then((label) => setSavedLocation({ lat, lng, label }));
      return;
    }
    const saved = localStorage.getItem(LOCATION_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSavedLocation({
          ...parsed,
          label: parsed.label || parsed.address || "Saved current location",
        });
      } catch {}
    }
  }, [params, t]);

  const categories = ["All", ...new Set(services.map((s) => s.category))];

  const filtered = services.filter((s) => {
    const matchSearch =
      s.name.toLowerCase().includes(q.toLowerCase()) ||
      s.category.toLowerCase().includes(q.toLowerCase());

    const matchCategory =
      activeCategory === "All" || s.category === activeCategory;

    return matchSearch && matchCategory;
  });

  const handleBook = (sid) => {
    if (!user) {
      toast.error(t("services.loginRequired"));
      nav("/login?next=/book/" + sid);
      return;
    }
    if (user.role !== "user") {
      toast.error(t("services.customersOnly"));
      return;
    }
    nav(`/book/${sid}`);
  };

  const SkeletonCard = () => (
    <div className="animate-pulse bg-white rounded-xl border border-[#E5E3DC] overflow-hidden">
      <div className="aspect-[16/10] bg-[#E5E3DC]" />
      <div className="p-6 space-y-3">
        <div className="h-4 w-20 bg-[#E5E3DC] rounded" />
        <div className="h-6 w-40 bg-[#E5E3DC] rounded" />
        <div className="h-4 w-full bg-[#E5E3DC] rounded" />
        <div className="h-4 w-3/4 bg-[#E5E3DC] rounded" />
        <div className="h-8 w-24 bg-[#E5E3DC] rounded mt-4" />
      </div>
    </div>
  );

  return (
    <><Seo title="Home Services" description="Browse trusted home service professionals — AC repair, plumbing, cleaning, painting, carpentry, pest control & more. Book online." />
    <JsonLd services={services} />
    <div className="min-h-screen flex flex-col bg-[#FAF9F7]">
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-8 md:py-20 flex-1 w-full">
        {/* Header */}
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.3em] text-[#9A7B4F] font-semibold">
            {t("services.badge")}
          </p>
          <h1 className="text-5xl lg:text-6xl font-light mt-4">
            {t("services.title")}
          </h1>
          {savedLocation && (
            <div className="mt-5 inline-flex items-center gap-2 rounded-sm border border-[#E5E3DC] bg-white px-4 py-2 text-sm text-[#646964]">
              <MapPin size={15} className="text-[#1C4532]" />
              Using {savedLocation.label}
            </div>
          )}
        </div>

        {/* Search */}
        <div className="mt-10 max-w-lg flex items-center gap-3 border border-[#E5E3DC] rounded-xl bg-white px-5 py-3 shadow-sm focus-within:ring-2 focus-within:ring-[#1C4532]/20 transition">
          <Search size={18} />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("services.searchPlaceholder")}
            className="border-0 bg-transparent shadow-none"
          />
        </div>

        {/* Categories */}
        <div className="mt-8 flex gap-3 overflow-x-auto pb-2">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2 rounded-full text-sm whitespace-nowrap transition
                ${
                  activeCategory === cat
                    ? "bg-[#1C4532] text-white"
                    : "bg-white border border-[#E5E3DC] text-[#6B6F6A] hover:bg-[#F3F1ED]"
                }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))
            : filtered.map((s) => {
                const Icon = iconMap[s.icon] || Hammer;

                return (
                  <article
                    key={s.id}
                    className="bg-white rounded-xl border border-[#E5E3DC] overflow-hidden shadow-sm hover:shadow-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer"
                    onClick={() => setSelectedService(s)}
                  >
                    {/* Image */}
                    <div className="relative aspect-[16/10] overflow-hidden">
                      {s.rating >= 4.5 && (
                        <span className="absolute top-3 left-3 bg-black text-white text-xs px-3 py-1 rounded-full">
                          {"⭐ " + t("services.topRated")}
                        </span>
                      )}
                      <img
                        src={s.image_url}
                        alt={s.name}
                        className="w-full h-full object-cover hover:scale-105 transition duration-500"
                      />
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <div className="flex justify-between">
                        <div>
                          <p className="text-xs text-[#8B8F88]">
                            {s.category}
                          </p>
                          <h3 className="text-xl text-[#1A1D1A]">
                            {s.name}
                          </h3>
                        </div>
                        <Icon size={20} />
                      </div>

                      <p className="text-sm text-[#6B6F6A] mt-3 line-clamp-3">
                        {s.description}
                      </p>

                      <div className="mt-5 flex justify-between items-center">
                        <span className="text-lg text-[#1C4532] font-medium">
                          ₹{s.base_price}
                        </span>

                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBook(s.id);
                          }}
                        >
                          {t("services.book")}
                          <ArrowRight size={14} className="ml-2" />
                        </Button>
                      </div>
                    </div>
                  </article>
                );
              })}
        </div>

        {/* Empty */}
        {!loading && filtered.length === 0 && (
          <p className="text-center py-20 text-[#8B8F88] text-lg">
            {t("services.noResults")}
          </p>
        )}
      </main>

      <Footer />

      {/* Modal */}
      {selectedService && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 relative animate-in fade-in zoom-in-95 duration-300">
            <button
              onClick={() => setSelectedService(null)}
              className="absolute top-3 right-3 text-gray-500"
            >
              ✕
            </button>

            <img
              src={selectedService.image_url}
              className="w-full h-52 object-cover rounded-lg"
            />

            <h2 className="text-2xl mt-4 text-[#1A1D1A]">
              {selectedService.name}
            </h2>

            <p className="text-[#6B6F6A] mt-2">
              {selectedService.description}
            </p>

            <div className="mt-4 flex justify-between items-center">
              <span className="text-xl text-[#1C4532] font-medium">
                ₹{selectedService.base_price}
              </span>

              <Button onClick={() => handleBook(selectedService.id)}>
                {t("services.bookNow")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
